/**
 * Onboarding wizard trigger helpers.
 *
 * Two patterns:
 *  - `resolveLoginDestination()` (Fix A): synchronous answer to "where should
 *    we navigate this user after login?". Used at the call-site BEFORE the
 *    navigate() call, so Dashboard never mounts for users who need the wizard.
 *  - `checkAndRedirectToOnboarding(navigate)` (legacy): post-navigate guard.
 *    Kept for any old call-sites; new code should use resolveLoginDestination.
 *
 * Per user direction (Q2 = b, 2026-02-27): the wizard is triggered when the
 * user has 0 user_persona OR 0 match_persona records. Both gates enforced.
 */
import { authAxios } from "./auth";

/**
 * Returns the URL to navigate to after a successful login.
 *  - "/onboarding" if the user is missing a user_persona or a match_persona.
 *  - `defaultDest` (usually "/dashboard") if both already exist.
 *
 * Never throws — on network error returns `defaultDest` so the user is
 * never blocked from reaching the app.
 */
export async function resolveLoginDestination(defaultDest = "/dashboard") {
  try {
    const res = await authAxios.get("/matches");
    const list = Array.isArray(res.data) ? res.data : res.data?.matches || [];
    const hasUP = list.some((m) => m.match_type === "user_persona");
    const hasMP = list.some((m) => m.match_type !== "user_persona");
    if (!hasUP || !hasMP) return "/onboarding";
    return defaultDest;
  } catch (e) {
    console.warn("Login destination check failed:", e?.message || e);
    return defaultDest;
  }
}

/** Legacy post-navigate redirect. New code should prefer `resolveLoginDestination`. */
export async function checkAndRedirectToOnboarding(navigate) {
  const dest = await resolveLoginDestination("/dashboard");
  if (dest === "/onboarding") navigate("/onboarding", { replace: true });
}

/**
 * Fire-and-forget telemetry helper used by OnboardingWizard.jsx.
 * Always emits to posthog if available, and mirrors to our backend so the
 * admin Journeys panel has its own data source.
 */
export function emitWizardEvent({
  event,
  section,
  screen = null,
  field = null,
  value = null,
  filled_field_count = null,
  extra = null,
}) {
  try {
    if (typeof window !== "undefined" && window.posthog?.capture) {
      window.posthog.capture(`onboarding_wizard_${event}`, {
        section, screen, field, value, filled_field_count, ...(extra || {}),
      });
    }
  } catch (e) { console.debug("onboarding analytics local emit failed:", e?.message || e); }
  // Mirror to backend — best-effort.
  authAxios
    .post("/analytics/onboarding-wizard/event", {
      event, section, screen, field, value, filled_field_count, extra,
    })
    .catch((e) => { console.debug("onboarding analytics backend emit failed:", e?.message || e); });
}
